import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { AddToHomeIcon, BookmarkIcon, DesktopIcon, MenuDotsIcon, ShareIcon } from './InstallIcons'
import type { InstallMode, InstallTarget } from '../../lib/install/device'

export interface InstallPanelProps {
  /** What this device can do. */
  mode: InstallMode
  /** The device the icon is for: the site button that was picked, else this one. */
  target: InstallTarget
  /** A button was picked for a different device than the one showing this page. */
  isOtherDevice: boolean
  /** This page's own address, to type in on the other device. */
  installAddress: string
  /** The app was installed during this visit. */
  isJustInstalled: boolean
  /** The browser's install dialog was closed without installing. */
  isDeclined: boolean
  onInstall: () => void
  /** How this computer bookmarks a page: a computer gets a bookmark, never the browser's app install. */
  bookmarkKeys: string
}

/** Where "Open the app" goes: the home screen when signed in, sign-in otherwise. */
const APP_HOME = '/'

const PRIMARY_BUTTON = 'inline-flex min-h-11 items-center justify-center rounded-xl px-8 text-sm font-medium tracking-wide transition-opacity hover:opacity-90'
const PRIMARY_STYLE = { background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' } as const
const NOTE_STYLE = { background: 'rgba(155,114,192,0.06)', border: '1px solid #E4D5EE' } as const

function OpenAppLink() {
  const { t } = useTranslation()
  return (
    <Link to={APP_HOME} className={PRIMARY_BUTTON} style={{ ...PRIMARY_STYLE, textDecoration: 'none' }}>
      {t('install.openApp')}
    </Link>
  )
}

interface Step {
  icon: ReactNode
  title: string
  /** Optional: a step that already says where to tap needs no second line. */
  hint?: string
}

function Steps({ heading, steps }: { heading: string; steps: Step[] }) {
  return (
    <section>
      <h2 className="mb-3 text-xs font-medium uppercase tracking-widest" style={{ color: '#9B72C0' }}>{heading}</h2>
      <ol className="space-y-3" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {steps.map((step) => (
          <li key={step.title} className="flex items-start gap-4 rounded-2xl px-4 py-4" style={{ background: 'white', border: '1.5px solid #E4D5EE' }}>
            <span className="flex shrink-0 items-center justify-center rounded-xl" style={{ width: 44, height: 44, color: '#3D1F62', background: 'rgba(155,114,192,0.08)' }}>
              {step.icon}
            </span>
            <div className="min-w-0">
              <p className="text-sm font-medium" style={{ color: '#1C1128' }}>{step.title}</p>
              {step.hint && <p className="mt-0.5 text-xs leading-relaxed" style={{ color: '#6B5F7A' }}>{step.hint}</p>}
            </div>
          </li>
        ))}
      </ol>
    </section>
  )
}

/**
 * The by-hand steps for each device: the phones' for when the browser has no
 * install dialog to open, the computer's bookmark always.
 */
function TargetSteps({ target, bookmarkKeys }: { target: InstallTarget; bookmarkKeys: string }) {
  const { t } = useTranslation()
  if (target === 'iphone') {
    return (
      <Steps
        heading={t('install.iosHeading')}
        steps={[
          { icon: <ShareIcon />, title: t('install.iosStep1'), hint: t('install.iosStep1Hint') },
          { icon: <AddToHomeIcon />, title: t('install.iosStep2'), hint: t('install.iosStep2Hint') },
        ]}
      />
    )
  }
  if (target === 'android') {
    return (
      <Steps
        heading={t('install.androidHeading')}
        steps={[
          // Step 1 names the three dots itself (Marta, 2026-11-02), so it has no hint.
          { icon: <MenuDotsIcon />, title: t('install.androidStep1') },
          { icon: <AddToHomeIcon />, title: t('install.androidStep2'), hint: t('install.androidStep2Hint') },
        ]}
      />
    )
  }
  return (
    <Steps
      heading={t('install.computerHeading')}
      steps={[
        { icon: <DesktopIcon />, title: t('install.computerStep1'), hint: t('install.computerStep1Hint') },
        { icon: <BookmarkIcon />, title: t('install.computerStep2', { keys: bookmarkKeys }), hint: t('install.computerStep2Hint') },
      ]}
    />
  )
}

const OTHER_DEVICE_TITLE = {
  iphone: 'install.otherDeviceIphone',
  android: 'install.otherDeviceAndroid',
  computer: 'install.otherDeviceComputer',
} as const satisfies Record<InstallTarget, string>

/** What sits under the heading, by state. */
function PanelBody({
  mode,
  target,
  isOtherDevice,
  installAddress,
  isJustInstalled,
  isDeclined,
  onInstall,
  bookmarkKeys,
}: InstallPanelProps) {
  const { t } = useTranslation()

  if (isJustInstalled) {
    return (
      <section className="space-y-4" aria-live="polite">
        <h2 className="text-base font-medium" style={{ color: '#1C1128' }}>{t('install.doneTitle')}</h2>
        <p className="text-sm leading-relaxed" style={{ color: '#6B5F7A' }}>{t('install.doneBody')}</p>
        <OpenAppLink />
      </section>
    )
  }
  // A button for another device: the icon cannot be put there from here, so
  // say where to go and show the steps that will be waiting.
  if (isOtherDevice) {
    return (
      <div className="space-y-8">
        <section className="space-y-2 rounded-2xl px-4 py-4" style={NOTE_STYLE}>
          <h2 className="text-sm font-medium" style={{ color: '#3D1F62' }}>{t(OTHER_DEVICE_TITLE[target])}</h2>
          <p className="text-sm leading-relaxed" style={{ color: '#6B5F7A' }}>
            {t(target === 'computer' ? 'install.otherDeviceBodyComputer' : 'install.otherDeviceBody', { address: installAddress })}
          </p>
        </section>
        <TargetSteps target={target} bookmarkKeys={bookmarkKeys} />
      </div>
    )
  }
  if (mode === 'installed') {
    return (
      <section className="space-y-4">
        <h2 className="text-base font-medium" style={{ color: '#1C1128' }}>{t('install.installedTitle')}</h2>
        <p className="text-sm leading-relaxed" style={{ color: '#6B5F7A' }}>{t('install.installedBody')}</p>
        <OpenAppLink />
      </section>
    )
  }
  // A computer gets a bookmark and a desktop shortcut, even where the browser
  // could install the app: an installed browser app is too much there (Andy,
  // 2026-11-02, the same call as The Quiet Oracle's). A link dragged onto the
  // desktop becomes a shortcut that opens the usual browser.
  if (target === 'computer') {
    return (
      <div className="space-y-6">
        <TargetSteps target="computer" bookmarkKeys={bookmarkKeys} />
        <OpenAppLink />
        <p className="text-sm leading-relaxed" style={{ color: '#6B5F7A' }}>
          {t('install.dragToDesktop')}{' '}
          <a href={APP_HOME} style={{ color: '#3D1F62', textDecoration: 'underline', textUnderlineOffset: 4 }}>
            {t('install.dragLinkName')}
          </a>
        </p>
      </div>
    )
  }
  if (mode === 'prompt') {
    return (
      <section className="space-y-3">
        {isDeclined ? (
          <p role="status" className="text-sm leading-relaxed" style={{ color: '#6B5F7A' }}>{t('install.declined')}</p>
        ) : (
          <button type="button" onClick={onInstall} className={PRIMARY_BUTTON} style={PRIMARY_STYLE}>
            {t('install.install')}
          </button>
        )}
      </section>
    )
  }
  if (mode === 'ios-safari') return <TargetSteps target="iphone" bookmarkKeys={bookmarkKeys} />
  if (mode === 'ios-other-browser') {
    return (
      <div className="space-y-8">
        <section className="space-y-2 rounded-2xl px-4 py-4" style={NOTE_STYLE}>
          <h2 className="text-sm font-medium" style={{ color: '#3D1F62' }}>{t('install.iosOtherTitle')}</h2>
          <p className="text-sm leading-relaxed" style={{ color: '#6B5F7A' }}>{t('install.iosOtherBody')}</p>
          <p className="text-sm leading-relaxed" style={{ color: '#6B5F7A' }}>{t('install.iosOtherHint')}</p>
        </section>
        <TargetSteps target="iphone" bookmarkKeys={bookmarkKeys} />
      </div>
    )
  }
  // 'unsupported': no dialog to open. On Android the browser menu usually
  // still installs it, so the steps lead and the bookmark tip is the fallback;
  // "this browser cannot" would contradict the steps above it.
  if (target === 'android') {
    return (
      <div className="space-y-6">
        <TargetSteps target="android" bookmarkKeys={bookmarkKeys} />
        <p className="text-sm leading-relaxed" style={{ color: '#6B5F7A' }}>{t('install.bookmarkTip')}</p>
      </div>
    )
  }
  return (
    <section className="space-y-2">
      <h2 className="text-base font-medium" style={{ color: '#1C1128' }}>{t('install.unsupportedTitle')}</h2>
      <p className="text-sm leading-relaxed" style={{ color: '#6B5F7A' }}>{t('install.unsupportedBody')}</p>
      <p className="text-sm leading-relaxed" style={{ color: '#6B5F7A' }}>{t('install.bookmarkTip')}</p>
    </section>
  )
}

/** The install page, for every state a device can be in. Presentational: the page decides the state. */
export function InstallPanel(props: InstallPanelProps) {
  const { t } = useTranslation()
  // Whatever is holding the page, the line is about the device the icon is for.
  const isComputer = props.target === 'computer'

  return (
    <div className="space-y-8">
      <header>
        <h1 className="font-display text-3xl leading-snug" style={{ color: '#1C1128', fontWeight: 400 }}>{t('install.title')}</h1>
        <p className="mt-3 text-base leading-relaxed" style={{ color: '#6B5F7A' }}>{t(isComputer ? 'install.whyComputer' : 'install.why')}</p>
        {props.target === 'iphone' && <p className="mt-2 text-base leading-relaxed" style={{ color: '#6B5F7A' }}>{t('install.whyIos')}</p>}
      </header>
      <PanelBody {...props} />
    </div>
  )
}
